import clsx from 'clsx';
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

interface RulerPickerProps {
    min: number;
    max: number;
    onChange?: (value: number) => void;
    defaultValue?: number;
    onSave: (leverage: number) => void;
}

const ITEM_WIDTH = 20;

const RulerPicker: React.FC<RulerPickerProps> = ({
    min,
    max,
    onChange,
    defaultValue,
    onSave,
}) => {
    const { t } = useTranslation();

    const containerRef = useRef<HTMLDivElement>(null);

    const [centerIndex, setCenterIndex] = useState(0);

    const [sidePadding, setSidePadding] = useState(0);

    const values = useMemo(() => {
        return Array.from({ length: max - min + 1 }, (_, i) => min + i);
    }, [max, min]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Padding để item đầu/cuối có thể vào đúng tâm
        const pad = container.clientWidth / 2 - ITEM_WIDTH / 2;
        setSidePadding(pad);

        const handleScroll = () => {
            // ✅ Với padding hai bên như trên, index ở tâm = round(scrollLeft / ITEM_WIDTH)
            const rawIndex = Math.round(container.scrollLeft / ITEM_WIDTH);
            const clamped = Math.min(Math.max(rawIndex, 0), values.length - 1);

            setCenterIndex(clamped);
            onChange?.(values[clamped]);
        };

        // đồng bộ ngay lần đầu
        handleScroll();

        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [values.length, onChange, values]);

    const handleSetValue = useCallback(
        (defaultValue: number) => {
            if (defaultValue === undefined || !containerRef.current) return;
            let idx = defaultValue - min;
            idx = Math.min(Math.max(idx, 0), values.length - 1);

            // ✅ với cơ chế padding này, tọa độ scroll để index vào giữa chỉ là idx * ITEM_WIDTH
            containerRef.current.scrollTo({
                left: idx * ITEM_WIDTH,
                behavior: 'auto',
            });
        },
        [min, values.length]
    );

    useLayoutEffect(() => {
        if (defaultValue === undefined || !containerRef.current) return;

        // index trong mảng từ defaultValue
        const id = setTimeout(() => {
            handleSetValue(defaultValue);
        }, 20);

        return () => clearTimeout(id);
    }, [defaultValue, handleSetValue]);

    return (
        <div>
            <div className="flex justify-between items-center">
                <button
                    className="text-white font-medium bg-[#1F1F1F] rounded-full px-2.5 py-1 uppercase"
                    onClick={() => {
                        containerRef.current?.scrollTo({
                            left: 0,
                            behavior: 'auto',
                        });
                        handleSetValue(min);
                    }}
                >
                    {t('MIN')}
                </button>

                <p className="text-[40px] font-medium">
                    {values[centerIndex]}X
                </p>

                <button
                    className="text-white font-medium bg-[#1F1F1F] rounded-full px-2.5 py-1 uppercase"
                    onClick={() => {
                        const last = values.length - 1;
                        containerRef.current?.scrollTo({
                            left: last * ITEM_WIDTH, // ✅ đưa item cuối vào giữa
                            behavior: 'auto',
                        });
                        handleSetValue(max);
                    }}
                >
                    {t('MAX')}
                </button>
            </div>

            <div className="relative w-full flex justify-center items-center h-[200px] overflow-hidden">
                {/* Marker center */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 flex flex-col items-center">
                    <svg width="13" height="70" viewBox="0 0 13 70" fill="none">
                        <path d="M2 1H11L6.5 5.5L2 1Z" fill="#000" />
                        <path
                            d="M6.5 4V69.5M2 1H11L6.5 5.5L2 1Z"
                            stroke="#000"
                        />
                    </svg>
                </div>

                <div
                    ref={containerRef}
                    className="flex overflow-x-scroll scrollbar-hide snap-x snap-mandatory w-full no-scrollbar"
                >
                    {/* padding trái */}
                    <div style={{ minWidth: sidePadding }} />

                    {values.map((val, index) => {
                        const distance = Math.abs(index - centerIndex);
                        const isCenter = distance === 0;
                        const isNear = distance === 1;
                        const isNear2 = distance === 2;
                        return (
                            <div
                                key={val}
                                className="flex flex-col items-center justify-end snap-center"
                                style={{ minWidth: ITEM_WIDTH }}
                            >
                                {/* số */}
                                <span
                                    className={clsx(
                                        'text-[8px] font-semibold transition-all duration-200 text-black',
                                        {
                                            'scale-125': isCenter,
                                            'scale-100': !isCenter,
                                        }
                                    )}
                                >
                                    {!isCenter && val}
                                </span>

                                {/* vạch */}
                                <div
                                    className={clsx(
                                        'mt-1 w-[2px] transition-all duration-200 bg-[#333]',
                                        {
                                            '!h-11 bg-black': isCenter,
                                            '!h-8': isNear,
                                            '!h-6': !isCenter && !isNear,
                                            '!h-[26px]': isNear2,
                                        }
                                    )}
                                />
                            </div>
                        );
                    })}

                    {/* padding phải */}
                    <div style={{ minWidth: sidePadding }} />
                </div>
            </div>

            <button
                className="w-full py-3 rounded-full bg-black text-white font-semibold"
                onClick={() => onSave(values[centerIndex])}
            >
                {t('Save')}
            </button>
        </div>
    );
};

export default RulerPicker;
